// src/pages/ResourcesPage.js
import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FileText, Shield, Accessibility, ArrowRight,
  Clock, Zap, Languages, Calculator
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

const ResourcesPage = () => {
  const navigate = useNavigate();
  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen pt-20"
    >
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Resources</h1>
              <p className="text-xl text-gray-600">
                Everything you need to make an informed decision
              </p>
            </motion.div>

            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-8"
            >
              <a 
                href="/ProductOnePager.pdf" 
                download="HousingAI-Product-One-Pager.pdf"
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer flex flex-col h-full"
              >
                <FileText className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Product One-Pager</h3>
                <p className="text-gray-600 mb-4">
                  Quick overview of features and benefits for procurement teams.
                </p>
                <span className="text-blue-600 font-medium flex items-center gap-1 mt-auto">
                  Download PDF <ArrowRight className="h-4 w-4" />
                </span>
              </a>

              <a 
                href="/SecurityBrief.pdf" 
                download="HousingAI-Security-Brief.pdf"
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer flex flex-col h-full"
              >
                <Shield className="h-12 w-12 text-green-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">Security Brief</h3>
                <p className="text-gray-600 mb-4">
                  Detailed security architecture, infrastructure compliance, and data policies.
                </p>
                <span className="text-green-600 font-medium flex items-center gap-1 mt-auto">
                  Download PDF <ArrowRight className="h-4 w-4" />
                </span>
              </a>

              <a 
                href="/WCAGChecklist.pdf" 
                download="HousingAI-WCAG-Checklist.pdf"
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer flex flex-col h-full"
              >
                <Accessibility className="h-12 w-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-bold mb-3">WCAG Checklist</h3>
                <p className="text-gray-600 mb-4">
                  Disability compliance documentation.
                </p>
                <span className="text-purple-600 font-medium flex items-center gap-1 mt-auto">
                  Download PDF <ArrowRight className="h-4 w-4" />
                </span>
              </a>
            </motion.div>

            {/* Interactive Tools */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.35 }}
              className="mt-16"
            >
              <h3 className="text-2xl font-bold mb-8 text-center">Interactive Tools</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <button
                  type="button"
                  onClick={() => navigate('/translate')}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer text-left"
                >
                  <Languages className="h-12 w-12 text-green-600 mb-4" />
                  <h3 className="text-xl font-bold mb-3">Translation Library</h3>
                  <p className="text-gray-600 mb-4">
                    Browse housing authority pages, forms, and official documents translated into 10+ languages, with side-by-side originals.
                  </p>
                  <span className="text-green-600 font-medium inline-flex items-center gap-1">
                    Explore the library <ArrowRight className="h-4 w-4" />
                  </span>
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/calculator')}
                  className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer text-left"
                >
                  <Calculator className="h-12 w-12 text-blue-600 mb-4" />
                  <h3 className="text-xl font-bold mb-3">Eligibility Calculator</h3>
                  <p className="text-gray-600 mb-4">
                    Residents can enter their ZIP, income, and household details to see which housing programs they may qualify for, with estimated rent.
                  </p>
                  <span className="text-blue-600 font-medium inline-flex items-center gap-1">
                    Try the calculator <ArrowRight className="h-4 w-4" />
                  </span>
                </button>
              </div>
            </motion.div>

            {/* Release Notes */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mt-16 bg-white rounded-3xl p-8 shadow-lg"
            >
              <h3 className="text-2xl font-bold mb-6">Latest Updates</h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-600 pl-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold">Version 2.7.0</h4>
                    <span className="text-sm text-gray-600">Apr 2026</span>
                  </div>
                  <p className="text-gray-600">Automated response-quality testing harness with deterministic fact checks and LLM-as-judge grading; ongoing tuning to reduce hallucination and improve retrieval.</p>
                </div>
                <div className="border-l-4 border-green-600 pl-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold">Version 2.6.0</h4>
                    <span className="text-sm text-gray-600">Mar 2026</span>
                  </div>
                  <p className="text-gray-600">Waltham Housing Authority onboarded; QR landing page for MassNAHRO conference.</p>
                </div>
                <div className="border-l-4 border-purple-600 pl-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold">Version 2.5.0</h4>
                    <span className="text-sm text-gray-600">Feb 2026</span>
                  </div>
                  <p className="text-gray-600">Sentiment-triggered staff escalation and automatic flagging of distressing, dangerous, or personal content in analytics.</p>
                </div>
                <div className="border-l-4 border-blue-600 pl-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold">Version 2.4.0</h4>
                    <span className="text-sm text-gray-600">Jan 2026</span>
                  </div>
                  <p className="text-gray-600">Public Translation Library launched at /translate with side-by-side originals.</p>
                </div>
                <div className="border-l-4 border-green-600 pl-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold">Version 2.3.0</h4>
                    <span className="text-sm text-gray-600">Dec 2025</span>
                  </div>
                  <p className="text-gray-600">Arlington Housing Authority onboarded; nightly crawl with vision-model OCR for scanned forms.</p>
                </div>
                <div className="border-l-4 border-purple-600 pl-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold">Version 2.2.0</h4>
                    <span className="text-sm text-gray-600">Nov 2025</span>
                  </div>
                  <p className="text-gray-600">Winchester Housing Authority onboarded; multilingual support expanded to 10 languages with voice input and output.</p>
                </div>
                <div className="border-l-4 border-blue-600 pl-4">
                  <div className="flex justify-between items-start mb-1">
                    <h4 className="font-semibold">Version 2.1.0</h4>
                    <span className="text-sm text-gray-600">Oct 2025</span>
                  </div>
                  <p className="text-gray-600">Initial launch with Salem and Concord Housing Authorities; multi-organization architecture with per-HA theming.</p>
                </div>
              </div>
            </motion.div>

            {/* Support Section */}
            <motion.div 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-16"
            >
              <h3 className="text-2xl font-bold mb-8 text-center">Onboarding & Support</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                  <Clock className="h-12 w-12 text-blue-600 mx-auto mb-3" />
                  <h4 className="font-semibold mb-2">3-Day Timeline</h4>
                  <p className="text-gray-600">From kick-off to go-live in just 3 days</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                  <Zap className="h-12 w-12 text-green-600 mx-auto mb-3" />
                  <h4 className="font-semibold mb-2">Minimal Effort</h4>
                  <p className="text-gray-600">Email policies, paste HTML snippet - we handle the rest</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg text-center">
                  <Shield className="h-12 w-12 text-purple-600 mx-auto mb-3" />
                  <h4 className="font-semibold mb-2">99.9% Uptime SLA</h4>
                  <p className="text-gray-600">Enterprise support with guaranteed response times</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ResourcesPage;

// ===== END OF src/pages/ResourcesPage.js =====